import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import LandingNavigation from '../components/LandingNavigation';
import LandingFooter from '../components/LandingFooter';
import '../styles/landing.css';

const DataDeletion = () => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (!email || !email.includes('@')) {
            setError('Please enter a valid email address.');
            return;
        }

        // In production, this would send a request to your backend
        // For now, we'll show a confirmation and direct users to email
        setSubmitted(true);
    };

    return (
        <div className="legal-page">
            <LandingNavigation />

            <div className="legal-container">
                <Link to="/" className="legal-back-link">
                    <ArrowLeft size={16} />
                    Back to Home
                </Link>

                <h1 className="legal-title">Data Deletion Request</h1>
                <p className="legal-updated">Delete your personal data from Vitisiti</p>

                <div className="legal-content">
                    {!submitted ? (
                        <>
                            <section className="legal-section">
                                <h2>Request Data Deletion</h2>
                                <p>
                                    In compliance with privacy regulations including GDPR and Meta Platform requirements,
                                    you have the right to request deletion of your personal data from our systems.
                                </p>
                                <p>
                                    When you request data deletion, we will remove:
                                </p>
                                <ul>
                                    <li>Your account information (name, email, password)</li>
                                    <li>Brand data and assets you have uploaded</li>
                                    <li>Generated creative content</li>
                                    <li>Usage history and analytics data</li>
                                    <li>Connected platform tokens and credentials</li>
                                    <li>All associated metadata</li>
                                </ul>
                            </section>

                            <section className="legal-section">
                                <h2>Submit Deletion Request</h2>
                                <p>
                                    Enter the email address associated with your Vitisiti account to request data deletion.
                                </p>

                                <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-4)' }}>
                                    {error && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-3)',
                                            background: 'rgba(239, 68, 68, 0.1)',
                                            border: '1px solid rgba(239, 68, 68, 0.3)',
                                            borderRadius: 'var(--radius-md)',
                                            color: 'var(--color-error)',
                                            fontSize: 'var(--font-size-sm)',
                                            marginBottom: 'var(--space-4)'
                                        }}>
                                            <AlertCircle size={16} />
                                            {error}
                                        </div>
                                    )}

                                    <div className="input-group">
                                        <label className="input-label">Email Address</label>
                                        <input
                                            type="email"
                                            className="input-field"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Enter your account email"
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            marginTop: 'var(--space-4)'
                                        }}
                                    >
                                        <Trash2 size={16} />
                                        Request Data Deletion
                                    </button>
                                </form>
                            </section>

                            <section className="legal-section">
                                <h2>Alternative: Delete via Email</h2>
                                <p>
                                    You can also request data deletion by sending an email to our privacy team:
                                </p>
                                <div className="legal-contact">
                                    <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <Mail size={16} />
                                        <a
                                            href="mailto:privacy@vitisiti.com?subject=Data%20Deletion%20Request"
                                            style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
                                        >
                                            privacy@vitisiti.com
                                        </a>
                                    </p>
                                    <p style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-2)' }}>
                                        Please include "Data Deletion Request" in the subject line along with
                                        the email address associated with your account.
                                    </p>
                                </div>
                            </section>

                            <section className="legal-section">
                                <h2>What Happens Next</h2>
                                <ul>
                                    <li>We will verify your identity and account ownership</li>
                                    <li>Your data will be deleted within 30 days of verification</li>
                                    <li>You will receive a confirmation email once deletion is complete</li>
                                    <li>Some data may be retained for legal compliance (anonymized)</li>
                                </ul>
                            </section>

                            <section className="legal-section">
                                <h2>For Meta Platform Users</h2>
                                <p>
                                    If you connected your Meta (Facebook/Instagram) account to Vitisiti and
                                    wish to revoke access:
                                </p>
                                <ul>
                                    <li>Go to Facebook Settings → Apps and Websites</li>
                                    <li>Find Vitisiti and click "Remove"</li>
                                    <li>This will revoke our access to your Meta data</li>
                                    <li>Submit a deletion request here to remove data already stored</li>
                                </ul>
                            </section>
                        </>
                    ) : (
                        <section className="legal-section">
                            <div style={{
                                textAlign: 'center',
                                padding: 'var(--space-8)'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    background: 'rgba(34, 197, 94, 0.1)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto var(--space-4)',
                                    color: 'var(--color-success)'
                                }}>
                                    <CheckCircle size={32} />
                                </div>

                                <h2 style={{ marginBottom: 'var(--space-4)', border: 'none', paddingBottom: 0 }}>
                                    Deletion Request Submitted
                                </h2>

                                <p style={{ maxWidth: '500px', margin: '0 auto var(--space-6)' }}>
                                    We have received your data deletion request for <strong>{email}</strong>.
                                    Our team will verify your request and process it within 30 days.
                                </p>

                                <div style={{
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-lg)',
                                    padding: 'var(--space-4)',
                                    textAlign: 'left',
                                    maxWidth: '400px',
                                    margin: '0 auto'
                                }}>
                                    <p style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>
                                        What happens next:
                                    </p>
                                    <ul style={{ margin: 0 }}>
                                        <li>You will receive a verification email shortly</li>
                                        <li>Follow the link to confirm your identity</li>
                                        <li>Your data will be deleted within 30 days</li>
                                        <li>You will receive a final confirmation</li>
                                    </ul>
                                </div>

                                <p style={{ marginTop: 'var(--space-6)', fontSize: 'var(--font-size-sm)' }}>
                                    Questions? Contact us at{' '}
                                    <a
                                        href="mailto:privacy@vitisiti.com"
                                        style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
                                    >
                                        privacy@vitisiti.com
                                    </a>
                                </p>

                                <Link
                                    to="/"
                                    className="btn btn-secondary"
                                    style={{ marginTop: 'var(--space-4)' }}
                                >
                                    Return to Home
                                </Link>
                            </div>
                        </section>
                    )}
                </div>
            </div>

            <LandingFooter />
        </div>
    );
};

export default DataDeletion;
